import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Calculator, 
  UtensilsCrossed, 
  Users, 
  MonitorSpeaker,
  Timer,
  Volume2,
  Grid3X3,
  Music,
  FileSearch,
  AlertTriangle,
  ArrowRightLeft,
  Sparkles,
  Wifi,
  ClipboardList
} from "lucide-react";

const ToolCard = ({ icon: Icon, title, description, href, suite, span = "" }) => {
  const suiteStyles = {
    planning: {
      badge: "suite-badge-planning",
      iconColor: "text-cyan-400",
      hoverBorder: "hover:border-cyan-500/30"
    },
    execution: {
      badge: "suite-badge-execution",
      iconColor: "text-yellow-400",
      hoverBorder: "hover:border-yellow-500/30"
    },
    solver: {
      badge: "suite-badge-solver",
      iconColor: "text-purple-400",
      hoverBorder: "hover:border-purple-500/30"
    }
  };

  const style = suiteStyles[suite];

  return (
    <Link to={href} className={`block ${span}`} data-testid={`tool-card-${href.replace(/\//g, '-').slice(1)}`}>
      <Card className={`tactical-card-interactive h-full tool-card ${style.hoverBorder}`}>
        <CardHeader className="pb-3">
          <div className={`p-3 sm:p-2.5 rounded-sm bg-secondary ${style.iconColor} w-fit`}>
            <Icon className="h-7 w-7 sm:h-5 sm:w-5" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-base sm:text-sm md:text-base mt-3 uppercase tracking-wide leading-snug whitespace-normal" style={{ wordBreak: 'keep-all', overflowWrap: 'normal' }}>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-muted-foreground text-sm sm:text-xs line-clamp-3">
            {description}
          </CardDescription>
        </CardContent>
      </Card>
    </Link>
  );
};

const SuiteHeader = ({ title, icon: Icon, color, description }) => (
  <div className="col-span-full flex items-center gap-3 mt-6 first:mt-0 mb-2">
    <div className={`p-2.5 sm:p-2 rounded-sm bg-secondary ${color}`}>
      <Icon className="h-6 w-6 sm:h-5 sm:w-5" strokeWidth={1.5} />
    </div>
    <div>
      <h2 className={`text-2xl sm:text-xl uppercase tracking-wider ${color}`}>{title}</h2>
      <p className="text-base sm:text-sm text-muted-foreground">{description}</p>
    </div>
  </div>
);

export const Dashboard = () => {
  return (
    <Layout>
      <div className="space-y-2">
        {/* Hero Section */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter lowercase mb-2" data-testid="dashboard-brand-title">
            evnt<span className="font-bold">kit</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Planning and delivery tools for event professionals — cover planning, on-site execution, and problem solving.
            All measurements default to metric with imperial conversion available.
          </p>
        </div>

        {/* Planning Suite */}
        <SuiteHeader 
          title="Planning Suite" 
          icon={Calculator} 
          color="text-cyan-400"
          description="Calculators and estimators for event preparation"
        />
        <div className="bento-grid">
          <ToolCard
            icon={Calculator}
            title="Room Capacity"
            description="Calculate maximum attendees based on room dimensions and seating arrangement"
            href="/planning/room-capacity"
            suite="planning"
          />
          <ToolCard
            icon={UtensilsCrossed}
            title="Catering Estimator"
            description="Waste-Zero food & beverage calculator with sustainability mode"
            href="/planning/catering"
            suite="planning"
          />
          <ToolCard
            icon={Users}
            title="Staffing Planner"
            description="Calculate staff requirements based on guest count and event duration"
            href="/planning/staffing"
            suite="planning"
          />
          <ToolCard
            icon={MonitorSpeaker}
            title="AV Tech Specs"
            description="Sightline check, screen sizing, off-axis verification, and projector brightness — for projectors and LED walls"
            href="/planning/av-specs"
            suite="planning"
          />
          <ToolCard
            icon={Wifi}
            title="WiFi Predictor"
            description="Calculate bandwidth requirements based on attendees and usage"
            href="/planning/wifi"
            suite="planning"
          />
          <ToolCard
            icon={Grid3X3}
            title="Floor Plan"
            description="2D layout tool to plan table and stage placement"
            href="/planning/floor-plan"
            suite="planning"
          />
          <ToolCard
            icon={Music}
            title="Dancefloor Calculator"
            description="Calculate ideal dancefloor size based on guest count"
            href="/planning/dancefloor-calculator"
            suite="planning"
          />
        </div>

        {/* Execution Suite */}
        <SuiteHeader 
          title="Execution Suite" 
          icon={Timer} 
          color="text-yellow-400"
          description="On-site tools for live event management"
        />
        <div className="bento-grid">
          <ToolCard
            icon={Timer}
            title="Sync Timer"
            description="Shareable countdown clock - sync with your entire team via code"
            href="/execution/timer"
            suite="execution"
          />
          <ToolCard
            icon={Volume2}
            title="Decibel Meter"
            description="Monitor venue noise levels with visual alerts at 90dB"
            href="/execution/decibel"
            suite="execution"
          />
          <ToolCard
            icon={ClipboardList}
            title="Dinner Service Tally"
            description="Tick off Starter, Main, Dessert per table — shareable & syncs across servers in real time"
            href="/execution/tally"
            suite="execution"
          />
        </div>

        {/* Problem Solver Suite */}
        <SuiteHeader 
          title="Problem Solver Suite" 
          icon={Sparkles} 
          color="text-purple-400"
          description="AI-powered tools for emergencies and conversions"
        />
        <div className="bento-grid">
          <ToolCard
            icon={FileSearch}
            title="Contract Scanner"
            description="AI analyses contracts to highlight hidden costs and red flags"
            href="/solver/contract"
            suite="solver"
          />
          <ToolCard
            icon={AlertTriangle}
            title="Emergency Script"
            description="AI-generated calm announcements for event emergencies"
            href="/solver/emergency"
            suite="solver"
          />
          <ToolCard
            icon={ArrowRightLeft}
            title="Unit Converter"
            description="Currency and unit conversions for international planners"
            href="/solver/converter"
            suite="solver"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
